import $ from 'jquery';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import AditoolsRenderer from '../modifiers/aditools-renderer.ts';
import Comparator from '../utils/comparator.ts';
import MessageFormat from '@messageformat/core';
import exportToExcel from '../utils/export-to-excel.ts';
import 'slickgrid/dist/styles/css/slick-icons.css';
import 'slickgrid/dist/styles/css/slick-alpine-theme.css';
import Sortable from 'sortablejs';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace globalThis {
    // eslint-disable-next-line no-var
    var Sortable: unknown;
  }
}
import { SlickDataView, SlickGrid, SlickRowSelectionModel } from 'slickgrid';

import type {
  GridOption,
  Column,
  ColumnSort,
  SlickEventData,
  OnRowsChangedEventArgs,
  OnHeaderCellRenderedEventArgs,
  OnHeaderRowCellRenderedEventArgs,
  OnHeaderMouseEventArgs,
  OnRowCountChangedEventArgs,
  SingleColumnSort,
  MultiColumnSort,
} from 'slickgrid';

globalThis.Sortable = Sortable;

const TRANSLATIONS = {
  en: {
    choose: 'Choose Parameters',
    all: 'All',
    table: 'Reset Table',
    sort: 'Sort By Newest',
    export: 'Download To Excel',
  },
  cn: {
    choose: '选择参数',
    all: '全部选择',
    table: '重置表格',
    sort: '按最新排序',
    export: '下载到 Excel',
  },
  jp: {
    choose: 'パラメータを選ぶ',
    all: 'すべて選択',
    table: 'リストを元に戻す',
    sort: '新着順にソート',
    export: 'エクセルにダウンロード',
  },
  ru: {
    choose: 'Choose Parameters',
    all: 'All',
    table: 'Reset Table',
    sort: 'Sort By Newest',
    export: 'Download To Excel',
  },
  /*
  ru: {
    choose: 'Выбрать Параметры',
    all: 'Все',
    table: 'Восстановить таблицу',
    sort: 'Сортировать по Новейшим',
    export: 'Загрузить в Excel',
  },
  */
};

// Our interfaces
export type DeviceData = Record<string, unknown>;

export type DeviceComparator<T> = (first: T, second: T) => 1 | 0 | -1;

export interface ColumnFilter<T> {
  populate(column: Column<T>): HTMLElement;
  update(control: HTMLElement): void;
  filter(value: unknown): boolean;
  unpack(value: string): void;
  pack(): string;
  reset(): void;
  hasValue(): boolean;
}
export interface FieldValues {
  value: string;
  display: string;
  match?: string;
}
export interface DeviceTableSort {
  field: string;
  sortAsc: boolean;
  comparator?: DeviceComparator<string>;
}
export interface SortColumnRef {
  columnId: string;
  sortAsc: boolean;
}
export interface PackedFilterValue {
  columnId: string;
  filter: string;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface DeviceColumn<T = any> extends Column {
  filter?: ColumnFilter<T>;
  columnValues?: FieldValues[];
  comparator?: DeviceComparator<string>;
  classification?: string;
  isDefault?: boolean;
  unit?: string | null;
  fieldContext?: string | null;
  minMaxTyp?: string | null;
}
export interface DeviceOptions<C extends Column> extends GridOption<C> {
  preSort?: DeviceTableSort[];
  parametersPanel?: boolean;
}

interface AditoolsDeviceTableArgs {
  data?: DeviceData[];
  columns?: DeviceColumn[];
  options?: DeviceOptions<DeviceColumn>;
  selectedPart?: string;
  rowSelected?: (part: string) => void;
  rowCountChanged?: (newCount: number) => void;
  summaryRowText?: string;
  onChooseParameters?: () => void;
  onAllParameters?: () => void;
  onResetParameters?: () => void;
  locale: string;
  sortColumns?: SortColumnRef[];
  sortChanged?: (sortColumns: SortColumnRef[]) => void;
  filters?: PackedFilterValue[];
  filterChanged?: (filters: PackedFilterValue[]) => void;
  dataSourceName?: string;
}

interface AditoolsDeviceTableSignature {
  Args: AditoolsDeviceTableArgs;
  Element: HTMLDivElement;
  Blocks: { default: [] };
}
export default class AditoolsDeviceTable extends Component<AditoolsDeviceTableSignature> {
  // Component properties
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  grid!: SlickGrid<any, DeviceColumn, DeviceOptions<DeviceColumn>> | undefined;
  dataView!: SlickDataView;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnFilters: Record<string, ColumnFilter<any>[]> = {};
  @tracked totalRows = 0;
  @tracked workingRows = 0;
  @tracked hasParametersPanel = false;
  renderer = AditoolsRenderer;

  headerColumnRows = 1;

  get _data(): DeviceData[] {
    return this.args.data || [];
  }

  get _columns(): DeviceColumn[] {
    return this.args.columns || [];
  }

  get _rowCountStatus(): string | undefined {
    let result = undefined;
    if (this.args.summaryRowText) {
      const mf = new MessageFormat('*');
      const msg = mf.compile(this.args.summaryRowText);
      result = msg({ count: this.workingRows.toString(), total: this.totalRows.toString() });
    }
    return result;
  }

  get _translations(): Record<string, string> {
    let locale: 'en' | 'cn' | 'jp' | 'ru' = 'en';
    if (
      this.args.locale === 'en' ||
      this.args.locale === 'cn' ||
      this.args.locale === 'jp' ||
      this.args.locale === 'ru'
    ) {
      locale = this.args.locale;
    }
    return TRANSLATIONS[locale as 'en' | 'cn' | 'jp' | 'ru'];
  }

  scrollSelectedPartToTop(
    selectedPart: string,
    dataView: SlickDataView,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    grid: SlickGrid<any, DeviceColumn, DeviceOptions<DeviceColumn>>,
  ): void {
    if (selectedPart) {
      let selectedRow = -1;
      const columns = grid.getColumns();
      const nameColumn = columns[0] as Column;
      if (columns.length > 1 && typeof nameColumn['field'] === 'string') {
        const fieldval = nameColumn.field;
        for (let i = 0; i < dataView.getLength(); i++) {
          if (dataView.getItem(i)[fieldval] === selectedPart) {
            selectedRow = i;
          }
        }
      }
      if (selectedRow !== -1) {
        grid.setSelectedRows([selectedRow]);
        setTimeout(() => {
          grid.scrollRowToTop(selectedRow - 1.75);
        }, 0);
      }
    }
  }

  sortRows(columns: ColumnSort[]): void {
    // Build the Columns to Sort (includes the pre-sort, if it is available)
    const preSort = this.args.options && this.args.options['preSort'];
    const colsToSort: DeviceTableSort[] = [];
    if (preSort && preSort.length > 0) {
      for (const presortItem of preSort) {
        colsToSort.push({ field: presortItem.field, sortAsc: presortItem.sortAsc, comparator: presortItem.comparator });
      }
    }
    if (columns.length === 0) {
      // Sort by Vintage when there are no columns selected for sort (both vintage and display_s1 refer to the same thing)
      // Legacy data may use 'vintage' but data from PST will use display_s1
      colsToSort.push({ field: 'vintage', sortAsc: false, comparator: Comparator.integerComparator });
      colsToSort.push({ field: 'display_s1', sortAsc: false, comparator: Comparator.integerComparator });
    } else {
      // Sorted Columns
      for (const sortColumn of columns) {
        const column = this.args.columns?.find((col) => col.id == sortColumn.columnId) as DeviceColumn;
        if (column.field) {
          colsToSort.push({
            field: column.field,
            sortAsc: sortColumn.sortAsc,
            comparator: column.comparator,
          });
        }
      }
    }

    // Sort the Data View
    this.dataView.sort((dataRow1: DeviceData, dataRow2: DeviceData) => {
      let result = 0;
      for (const column of colsToSort) {
        const field = column.field;
        let comparator: DeviceComparator<string> = Comparator.textComparator;
        const specificComparator = column.comparator;
        if (specificComparator) {
          comparator = specificComparator;
        }
        const sign = column.sortAsc ? 1 : -1;
        const value1 = dataRow1[field],
          value2 = dataRow2[field];
        result = comparator(value1 as string, value2 as string) * sign;
        if (result !== 0) {
          return result;
        }
      }
      return result;
    }, true);
  }

  findVisibleColumn(e: Event | SlickEventData, gridObj: SlickGrid): number {
    const cell = gridObj.getCellFromEvent(e);
    const columns = gridObj.getColumns();
    let index = 0; //set to zero at default, for the case where there is no column in the right side of the table.
    if (cell) {
      for (let i = 0; i < columns.length; i++) {
        if (gridObj.getCellNode(cell.row, i + 1)) {
          index = i + 1;
          break;
        }
      }
    }
    return index;
  }

  getDesiredRowObject(e: Event | SlickEventData, gridObj: SlickGrid): HTMLElement | null {
    const cell = gridObj.getCellFromEvent(e);
    let result;
    if (cell) {
      if (cell.cell === 0) {
        //left table row is hovered, gets right table row object
        const columnIndex = this.findVisibleColumn(e, gridObj);
        result = gridObj.getCellNode(cell.row, columnIndex)?.parentElement;
      } else {
        result = gridObj.getCellNode(cell.row, 0)?.parentElement;
      }
    }
    return result || null;
  }

  getSortColumnRef(sortCols: ColumnSort[]): SortColumnRef[] {
    const sortColumnRefs: SortColumnRef[] = [];
    for (const column of sortCols) {
      const sortCol = column.sortCol;
      if (sortCol && sortCol.id) {
        sortColumnRefs.push({
          columnId: sortCol.id.toString(),
          sortAsc: column.sortAsc,
        });
      }
    }
    return sortColumnRefs;
  }

  getSortColumns(sortColRefs: SortColumnRef[], columns: Column[]): ColumnSort[] {
    const sortColumns: ColumnSort[] = [];
    for (const columnRef of sortColRefs) {
      const column = columns?.find((column) => column.id === columnRef.columnId);
      if (column) {
        sortColumns.push({
          columnId: column.id,
          sortAsc: columnRef.sortAsc,
        });
      }
    }
    return sortColumns;
  }

  getPackedFilters(columns: Column[]): PackedFilterValue[] {
    const packedFilters: PackedFilterValue[] = [];

    if (this.grid) {
      for (let i = 0; i < columns.length; i++) {
        const column = columns[i] as DeviceColumn;
        if (column.id && column.filter) {
          packedFilters.push({ columnId: column.id.toString(), filter: column.filter.pack() });
        }
      }
    }

    return packedFilters;
  }

  generateTitleFromColumn(column: DeviceColumn): string {
    let numberedMultiColumnSortElem = '';
    const cssRoot = document.querySelector(':root') as HTMLElement;
    if (column.sortable) {
      const options = this.grid?.getOptions();
      numberedMultiColumnSortElem =
        options && options.numberedMultiColumnSort ? '<span class="slick-sort-indicator-numbered"></span>' : '';
    }
    let fieldContextRow = '';
    if (typeof column.fieldContext !== 'undefined') {
      fieldContextRow = `<span class="slick-column-context">${column.fieldContext}</span>`;
      if (this.headerColumnRows < 2) {
        this.headerColumnRows = 2;
      }
    }
    if (typeof column.unit != 'undefined' && column.unit !== null) {
      this.headerColumnRows =
        this.headerColumnRows === 3 || (this.headerColumnRows === 2 && typeof column.fieldContext !== 'undefined')
          ? 3
          : 2;
      const unitText = column.unit && column.unit != 'n/a' ? column.unit : '';
      const minMaxText = column.minMaxTyp ?? '';
      let contextText = '&nbsp;';
      if (unitText !== '' && minMaxText !== '') {
        contextText = `${minMaxText} | ${unitText}`;
      } else if (unitText !== '') {
        contextText = unitText;
      }
      cssRoot.style.setProperty('--alpine-header-name-row-count', this.headerColumnRows.toString());
      return (
        // Avoid newlines in the generated HTML
        '<div class="slick-header-column-group"><div class="slick-column-first-row">' +
        `<span class="slick-column-name">${column.name}</span>` +
        `<span class="slick-sort-indicator multi-row"></span>${numberedMultiColumnSortElem}</div>` +
        fieldContextRow +
        `<span class="slick-column-unit">${contextText}</span></div>`
      );
    } else {
      cssRoot.style.setProperty('--alpine-header-name-row-count', this.headerColumnRows.toString());
      if (column.sortable) {
        return (
          `<div class="slick-header-column-group"><div class="slick-column-first-row">` +
          `<span class="slick-column-name">${column.name}</span><span class="slick-sort-indicator"></span>${numberedMultiColumnSortElem}</div>${fieldContextRow}</div>
        `
        );
      } else {
        return `<div class="slick-header-column-group"><span class="slick-column-name">${column.name}</span>${fieldContextRow}</div>
        `;
      }
    }
  }

  updateCheckboxContainerEvents(column: DeviceColumn): void {
    const filterClass = column.filter;
    // add class to checkbox-container to overlay, then hide
    $('#checkbox-container' + column.id).addClass('overlay-checkbox-container');
    $('#checkbox-container' + column.id).hide();

    // handler for checkbox events
    const handleCheckbox = (e: Event) => {
      if (filterClass && this.grid) {
        filterClass.update(e.target as HTMLInputElement);
        // Set args.filters when the filter on a column is changed
        if (column.id) {
          if (this.args.filterChanged) {
            const columns = this.grid.getColumns();
            if (columns) {
              this.args.filterChanged(this.getPackedFilters(columns));
            }
          }
        }
        this.dataView.refresh();
      }
    };
    // attach event to checkbox
    document.getElementById('cbSelectAll_' + column.id)?.addEventListener('click', handleCheckbox);
    document.querySelectorAll('.cbfilter_' + column.id)?.forEach((cb) => {
      cb.addEventListener('click', handleCheckbox);
    });

    const filterToggle = $('#checkbox-filter-toggle' + column.id);
    if (filterToggle[0]) {
      const deviceTable = $('#device-table');
      const header = $('.slick-header-columns');

      filterToggle.off('mouseover');
      filterToggle.on('mouseover', function () {
        const newLeft = (filterToggle?.offset()?.left as number) - (deviceTable?.offset()?.left as number) + 11,
          newTop =
            (deviceTable?.position().top as number) +
            (header?.outerHeight() as number) +
            (filterToggle?.outerHeight() as number) +
            4;

        $('#checkbox-container' + column.id).css({
          position: 'absolute',
          left: `${newLeft}px`,
          top: `${newTop}px`,
        });

        $('#checkbox-container' + column.id).show();
      });

      $('#checkbox-filter-toggle' + column.id).off('mouseleave');
      $('#checkbox-filter-toggle' + column.id).on('mouseleave', function () {
        $('#checkbox-container' + column.id).hide();
      });

      $('#checkbox-container' + column.id).off('mouseover');
      $('#checkbox-container' + column.id).on('mouseover', function () {
        $('#checkbox-container' + column.id).show();
      });

      $('#checkbox-container' + column.id).off('mouseleave');
      $('#checkbox-container' + column.id).on('mouseleave', function () {
        $('#checkbox-container' + column.id).hide();
      });
    }
  }

  forceUpdateRowCountIfNeeded(): Promise<void> {
    return Promise.resolve()
      .then(() => {
        return this.dataView;
      })
      .then((dataView) => {
        const dataViewLength = dataView.getLength();
        if (this.workingRows !== dataViewLength) {
          const onRowsChangedArgs: OnRowCountChangedEventArgs = {
            previous: this.workingRows,
            current: dataViewLength,
            itemCount: dataViewLength,
            dataView: dataView,
            callingOnRowsChanged: true,
          };
          dataView.onRowCountChanged.notify(onRowsChangedArgs);
        }
        return;
      });
  }

  @action inserted(): void {
    const self = this; //eslint-disable-line @typescript-eslint/no-this-alias
    // Create element-scope objects

    console.log('Sortable: ' + JSON.stringify(Sortable));

    // Initialize CSS Variable for Header
    const cssRoot = document.querySelector(':root') as HTMLElement;
    this.headerColumnRows = 1;
    cssRoot.style.setProperty('--alpine-header-name-row-count', this.headerColumnRows.toString());

    const slickGridOptions = Object.assign({}, this.args.options);
    if (slickGridOptions['preSort']) {
      delete slickGridOptions['preSort'];
    }
    if (slickGridOptions['parametersPanel']) {
      delete slickGridOptions['parametersPanel'];
    }

    if (slickGridOptions.multiColumnSort === true) {
      slickGridOptions.numberedMultiColumnSort = true;
    }
    slickGridOptions.tristateMultiColumnSort = true;

    this._columns.forEach((c) => c.filter?.reset());
    this.hasParametersPanel = !!(this.args.options ? this.args.options['parametersPanel'] : false);
    this.dataView = new SlickDataView({ inlineFilters: false });
    this.grid = new SlickGrid('#device-table', this.dataView, this._columns, slickGridOptions);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /// @ts-ignore We don't know how to explicitly invoke node_modules/@types/slickgrid/slick.rowselectionmodel.d.ts
    this.grid.setSelectionModel(new SlickRowSelectionModel({ selectActiveRow: true }));

    this.dataView.onRowCountChanged.subscribe((/*_e: any, args: any*/) => {
      if (this.grid) {
        if (this.args.rowCountChanged) {
          this.args.rowCountChanged(this.dataView.getLength());
        }
        this.workingRows = this.dataView.getLength();
        this.grid.updateRowCount();
        this.grid.render();
      }
    });
    this.dataView.onRowsChanged.subscribe((_e: SlickEventData, data: OnRowsChangedEventArgs) => {
      if (this.grid) {
        this.grid.invalidateRows(data.rows);
        this.grid.render();
        if (this.args.selectedPart) {
          this.scrollSelectedPartToTop(this.args.selectedPart, this.dataView, this.grid);
        }
      }
    });

    // Selection handling

    this.grid.onSelectedRowsChanged.subscribe(
      (
        _e: SlickEventData,
        data: {
          rows: number[];
          grid: { getColumns: () => { field?: string }[] };
        },
      ) => {
        const row = this.dataView.getItem(data.rows[0] as number);
        if (row) {
          const column = data.grid.getColumns()[0] as { field: string };
          const newSelection = row[column.field] as string;
          if (this.args.rowSelected) {
            this.args.rowSelected(newSelection);
          } else {
            if ($('.slick-cell.selected').hasClass('selected')) {
              $('.slick-cell.selected').removeClass('selected');
            }
          }
        }
      },
    );

    // Custom Row Classes, Disabling

    {
      this.dataView.getItemMetadata = function (rowIndex: number) {
        let result = {
          selectable: true,
          focusable: true,
          cssClasses: '',
        };
        if (self.grid) {
          // Get the row object.
          const rowObj = self.grid.getData<SlickDataView>().getItem(rowIndex);

          // Check if the row object is populated.
          if (rowObj) {
            // Disable the row if it's 'not allowed'.
            if (rowObj['disallowed']) {
              result = {
                selectable: rowObj['selectable'] ? (rowObj['selectable'] as boolean) : false,
                focusable: rowObj['focusable'] ? (rowObj['focusable'] as boolean) : false,
                cssClasses: 'disabled ' + (rowObj['cssClasses'] ? rowObj['cssClasses'] : ''),
              };
            } else {
              result = {
                selectable: rowObj['selectable'] ? (rowObj['selectable'] as boolean) : true,
                focusable: rowObj['focusable'] ? (rowObj['focusable'] as boolean) : true,
                cssClasses: rowObj['cssClasses'] ? (rowObj['cssClasses'] as string) : '',
              };
            }
          }
        }
        return result;
      };
    }

    // Filtering
    $(this.grid.getHeaderRow()).on('input', ':input', this, function () {
      if (self.grid) {
        const column = self.grid.getColumns()[
          self.grid.getColumnIndex(this.getAttribute('data-column-id'))
        ] as DeviceColumn;
        if (column.filter) {
          column.filter.update(this);
        }
        self.dataView.refresh();
      }
    });

    $(this.grid.getHeaderRow()).on('change', ':input', this, function () {
      if (self.grid) {
        const column = self.grid.getColumns()[
          self.grid.getColumnIndex(this.getAttribute('data-column-id'))
        ] as DeviceColumn;
        if (column.filter) {
          column.filter.update(this);

          // Set args.filters when the filter on a column is changed
          if (column.id) {
            if (self.args.filterChanged) {
              self.args.filterChanged(self.getPackedFilters(self.grid.getColumns()));
            }
          }
        }
        self.dataView.refresh();
      }
    });

    this.grid.onHeaderCellRendered.subscribe((_e: SlickEventData, data: OnHeaderCellRenderedEventArgs) => {
      $(data.node).empty();
      $(self.generateTitleFromColumn(data.column as DeviceColumn)).appendTo(data.node);
      self.forceUpdateRowCountIfNeeded();
    });

    // remove checkbox container first in the device-table-overlays
    $('#device-table-overlays').empty();
    this.grid.onHeaderRowCellRendered.subscribe((_e: SlickEventData, data: OnHeaderRowCellRenderedEventArgs) => {
      $(data.node).empty();
      const column = data.column as DeviceColumn;
      const filterClass = column.filter;
      const filters = this.args.filters;

      if (filters && filters.length > 0) {
        const packedFilter = filters?.find((filter) => filter.columnId === column.id);
        if (filterClass) {
          if (!filterClass.hasValue()) {
            if (packedFilter) {
              filterClass.unpack(packedFilter.filter);
            } else {
              filterClass.reset();
            }
          }
        }
      } else if (filters === undefined) {
        if (filterClass) {
          if (!filterClass.hasValue()) {
            filterClass.reset();
          }
        }
      }

      const selection = filterClass ? filterClass.populate(column) : $();
      if (typeof column.id === 'string') {
        $(selection).data('columnId', column.id).appendTo(data.node);
      }

      // update checkbox and events after appending to column row header
      this.updateCheckboxContainerEvents(column);
    });

    // Hovering
    this.grid.onMouseEnter.subscribe((_e: SlickEventData, args: OnHeaderMouseEventArgs) => {
      const rowObj = this.getDesiredRowObject(_e, args.grid);
      if (rowObj) {
        $(rowObj).addClass('row-hover');
        if (!$(rowObj).hasClass('even')) {
          $(rowObj).removeClass('odd');
        }
      }
    });
    this.grid.onMouseLeave.subscribe((_e: SlickEventData, args: OnHeaderMouseEventArgs) => {
      const rowObj = this.getDesiredRowObject(_e, args.grid);
      if (rowObj) {
        $(rowObj).removeClass('row-hover');
        if (!$(rowObj).hasClass('even')) {
          $(rowObj).addClass('odd');
        }
      }
    });

    // Sorting

    this.grid.onSort.subscribe((_e: SlickEventData, data: SingleColumnSort | MultiColumnSort) => {
      const sortCols = data.multiColumnSort
        ? data.sortCols
        : [{ columnId: data.columnId, sortAsc: data.sortAsc, sortCol: data.sortCol }]; // Array of one ColumnSort
      if (this.grid && sortCols) {
        this.sortRows(sortCols);

        // Set sortColumns via callback
        if (this.args.sortChanged) {
          this.args.sortChanged(this.getSortColumnRef(sortCols));
        }

        this.grid.invalidate();
        this.grid.render();
      }
    });

    // Turn on the lights!
    this.grid.init();

    // All of the code so far in the modifier has been happening _during_ rendering.
    // We're set up, but we don't want to perform a DataView update while we're still rendering, so punt it a micro-cycle.
    Promise.resolve().then(() => {
      this.dataView.beginUpdate();
      this.dataView.setItems(this._data);
      this.sortRows([]);
      this.totalRows = this._data.length;

      // NOTE: The callback for setFilter is actually compiled by SlickGrid, not by Babel.
      // Implications - it needs to be pure vanilla ES5, without accessing a closure.
      // We use setFilterArgs so we can get at the rest of everything
      this.dataView.setFilterArgs({
        selfRef: this,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.dataView.setFilter((item: any, args: any) => {
        const actualArgs = args as { selfRef: AditoolsDeviceTable };
        const actualItem = item as DeviceData;
        if (actualArgs.selfRef.grid) {
          const columns = actualArgs.selfRef.grid.getColumns() as DeviceColumn[];
          return columns.every((column) => {
            return !column.filter || !column.field || column.filter.filter(actualItem[column.field]); // True if either filter or field doesn't exist or it passes
          });
        } else {
          return true;
        }
      });

      const sortColRefs = this.args.sortColumns;
      if (sortColRefs && sortColRefs.length > 0) {
        if (this.grid) {
          this.grid.setSortColumns(sortColRefs);
          this.sortRows(this.getSortColumns(sortColRefs, this.grid.getColumns()));
        }
      }

      this.dataView.endUpdate();
      if (this.grid) {
        this.dataView.syncGridSelection(this.grid as SlickGrid, true);
      }
      window.addEventListener('resize', this.screenResized);
      // Delay needed on resize so that Firefox gets benefit - the need for it is a hack, anyway
      setTimeout(() => {
        this.screenResized();
      }, 0);
    });
  }

  @action destroying(): void {
    window.removeEventListener('resize', this.screenResized);
    if (this.grid) {
      $(this.grid.getHeaderRow()).off('change keyup', ':input');
      this.grid.destroy();
      this.grid = undefined;
    }
  }

  @action screenResized(): void {
    if (this.grid) {
      this.grid.resizeCanvas();
    }
  }

  @action resetSort(): void {
    if (this.grid) {
      this.sortRows([]);
      this.grid.setSortColumns([]);
      if (this.args.sortChanged) {
        this.args.sortChanged([]);
      }
    }
  }

  @action tableRowUpdated(event: Event): void {
    const custEvent = event as CustomEvent<{ id: string }>;
    if (custEvent.detail && custEvent.detail.id) {
      const row = this.dataView.getItemById(custEvent.detail.id);
      this.dataView.updateItem(custEvent.detail.id, row);
    }
  }

  // The following invoke callbacks that change the columns array used by the table

  @action chooseParameters(): void {
    if (this.args.onChooseParameters) {
      this.args.onChooseParameters();
    }
  }

  @action allParameters(): void {
    if (this.args.onAllParameters) {
      this.args.onAllParameters();
    }
  }

  @action resetParameters(): void {
    if (this.args.onResetParameters) {
      this.args.onResetParameters();
    }
  }

  @action exportTable(): void {
    if (this.grid) {
      const rows = this.grid.getData<SlickDataView>();
      exportToExcel.exportTable(
        this.grid.getColumns(),
        rows.getFilteredItems(),
        this.args.locale,
        this.args.dataSourceName,
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    AditoolsDeviceTable: typeof AditoolsDeviceTable;
  }
}
